import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { useCallback } from "react";
import { useCanvasId } from "@/utils/utils";

/**
 * Hook to initialize MCP and restore Claude credentials on container startup.
 * Polls health endpoint, restores auth credentials from database, then calls init.
 */
export const useInitMcp = () => {
  const canvasId = useCanvasId();
  const { session } = useSession();

  const initMcp = useCallback(async () => {
    if (!canvasId || !session) {
      return;
    }

    const maxAttempts = 10;
    const initialDelay = 1000;
    const maxDelay = 5000;
    const BACKEND_URL = import.meta.env.VITE_BACKEND_BASE_URL;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const token = await session.getToken({ template: "tempo-claude-code" });
        if (!token) continue;
        const authHeader = `Bearer ${token}`;

        await axios.get(
          `${BACKEND_URL}/canvases/${canvasId}/claude-code/health?authHeader=${encodeURIComponent(authHeader)}`,
          { timeout: 2000 },
        );

        // Restore Claude credentials from database (if stored)
        await axios.get(
          `${BACKEND_URL}/canvases/${canvasId}/claude-code/ensureAuth?authHeader=${encodeURIComponent(authHeader)}`,
        );

        // Configure MCP server
        await axios.post(
          `${BACKEND_URL}/canvases/${canvasId}/claude-code/init?authHeader=${encodeURIComponent(authHeader)}`,
        );
        return;
      } catch {
        if (attempt < maxAttempts) {
          const delay = Math.min(
            initialDelay * Math.pow(2, attempt - 1),
            maxDelay,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }
  }, [canvasId, session]);

  return initMcp;
};
